import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import api from '../api/api';

// Valori di ore settimanali supportati
const ORE_SETT_OPTIONS = [18, 20, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40];

interface OreMapping {
  martedi: number;
  mercoledi: number;
  giovedi: number;
  venerdi: number;
  sabato: number;
  lunedi: number;
  domenica: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  negozio: string;
}

const AggiuntaDipendenteMomentaneoDialog: React.FC<Props> = ({ open, onClose, onCreated, negozio }) => {
  const [nome, setNome] = useState('');
  const [oreSettimanali, setOreSettimanali] = useState<number>(36);
  const [livello, setLivello] = useState<string>('');
  const [dataAssunzione, setDataAssunzione] = useState('');
  const [dataFineContratto, setDataFineContratto] = useState('');
  const [oreMapping, setOreMapping] = useState<OreMapping | null>(null);
  const [, setLoadingMapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carica il mapping ore quando cambia oreSettimanali o si apre il dialog
  useEffect(() => {
    if (!open) return;
    fetchOreMapping(oreSettimanali);
  }, [oreSettimanali, open]);

  const fetchOreMapping = async (ore: number) => {
    setLoadingMapping(true);
    try {
      const response = await api.get<OreMapping>(`/dipendenti-momentanei/ore-mapping/${ore}`);
      setOreMapping(response.data);
    } catch (err) {
      console.error('Errore caricamento ore mapping:', err);
      setOreMapping(null);
    } finally {
      setLoadingMapping(false);
    }
  };

  const handleReset = () => {
    setNome('');
    setOreSettimanali(36);
    setLivello('');
    setDataAssunzione('');
    setDataFineContratto('');
    setOreMapping(null);
    setError(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!nome.trim()) {
      setError('Il nome del dipendente è obbligatorio.');
      return;
    }
    if (!livello) {
      setError('Il livello è obbligatorio.');
      return;
    }
    if (!dataAssunzione) {
      setError('La data di assunzione è obbligatoria.');
      return;
    }
    if (!dataFineContratto) {
      setError('La data fine contratto è obbligatoria.');
      return;
    }

    setSaving(true);
    setError(null);

    const payload: Record<string, any> = {
      neg: negozio,
      nome: nome.trim() + '*',
      oreSettimanali,
      livello: livello ? parseInt(livello) : null,
      dataAssunzione,
      dataFineContratto: dataFineContratto || null,
    };

    // Includi il mapping giornaliero se disponibile
    if (oreMapping) {
      payload.lunedi = oreMapping.lunedi;
      payload.martedi = oreMapping.martedi;
      payload.mercoledi = oreMapping.mercoledi;
      payload.giovedi = oreMapping.giovedi;
      payload.venerdi = oreMapping.venerdi;
      payload.sabato = oreMapping.sabato;
      payload.domenica = oreMapping.domenica;
    }

    try {
      await api.post('/dipendenti-momentanei', payload);
      handleReset();
      onCreated();
    } catch (err: any) {
      const msg = err.response?.data || err.message || 'Errore durante la creazione del dipendente momentaneo.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Aggiungi Dipendente Momentaneo</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Cognome e Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value.toUpperCase())}
            fullWidth
            helperText="Cognome e nome completi come da documento d'identità"
            autoFocus
          />

          <TextField
            label="Negozio"
            value={negozio}
            InputProps={{ readOnly: true }}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Ore Settimanali</InputLabel>
            <Select
              value={oreSettimanali}
              label="Ore Settimanali"
              onChange={(e) => setOreSettimanali(Number(e.target.value))}
            >
              {ORE_SETT_OPTIONS.map((ore) => (
                <MenuItem key={ore} value={ore}>{ore}h</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Livello"
            value={livello}
            onChange={(e) => setLivello(e.target.value)}
            type="number"
            inputProps={{ min: 1, max: 10 }}
            fullWidth
          />

          <TextField
            label="Data Assunzione"
            type="date"
            value={dataAssunzione}
            onChange={(e) => setDataAssunzione(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="Data Fine Contratto"
            type="date"
            value={dataFineContratto}
            onChange={(e) => setDataFineContratto(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Annulla</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !nome.trim() || !livello || !dataAssunzione || !dataFineContratto}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Aggiungi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AggiuntaDipendenteMomentaneoDialog;
